import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShieldCheck, Phone, RefreshCw, CheckCircle2, Smartphone } from 'lucide-react';
import { Logo } from './Logo';
import { toast } from 'sonner';

interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function OTPModal({ isOpen, onClose, onSuccess }: OTPModalProps) {
  const [step, setStep] = React.useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [otp, setOtp] = React.useState(['', '', '', '', '', '']);
  const [isSending, setIsSending] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [timer, setTimer] = React.useState(0);

  const otpInputs = React.useRef<(HTMLInputElement | null)[]>([]);

  React.useEffect(() => {
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.length < 10) {
      toast.error('Invalid Phone Number', {
        description: 'Please enter a valid Nigerian phone number.'
      });
      return;
    }

    setIsSending(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSending(false);
    setStep('otp');
    setTimer(60);
    toast.success('Code Sent', {
      description: `A 6-digit verification code has been sent to ${phoneNumber}`
    });
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullOtp = otp.join('');
    if (fullOtp.length !== 6) return;

    setIsVerifying(true);
    // Simulate verification
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (fullOtp === '123456' || true) { // Simulating always success for demo
      setIsVerifying(false);
      onSuccess();
      toast.success('Identity Verified', {
        description: 'Welcome to the Sentlo trusted community!'
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            <div className="p-8">
              <div className="flex flex-col items-center mb-8">
                <div className="flex items-center justify-between w-full mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-xl font-black uppercase tracking-tight text-zinc-900">Verification</h2>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
                
                <Logo size="md" showSlogan={true} />
              </div>

              {step === 'phone' ? (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
                    <Smartphone className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 leading-relaxed font-medium">
                      To maintain a safe community, all contributors must verify their mobile number via OTP.
                    </p>
                  </div>

                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <input
                          type="tel"
                          required
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-4 pl-12 pr-4 text-lg font-bold focus:ring-4 focus:ring-green-600/5 focus:border-green-600 outline-hidden transition-all placeholder:text-zinc-300"
                          placeholder="e.g. 0801 234 5678"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d+]/g, ''))}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSending || phoneNumber.length < 10}
                      className="w-full bg-green-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-700 transition-all shadow-lg shadow-green-600/20 disabled:opacity-50 disabled:shadow-none"
                    >
                      {isSending ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>Send Verification Code</>
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-sm text-zinc-500">We've sent a 6-digit code to</p>
                    <p className="text-sm font-black text-zinc-900">{phoneNumber}</p>
                    <button 
                      onClick={() => setStep('phone')}
                      className="text-[10px] font-black uppercase tracking-widest text-green-600 mt-1 hover:text-green-700"
                    >
                      Change Number
                    </button>
                  </div>

                  <form onSubmit={handleVerifyOTP} className="space-y-6">
                    <div className="flex justify-between gap-2">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpInputs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          className="w-12 h-14 bg-zinc-50 border-2 border-zinc-200 rounded-xl text-center text-xl font-black text-green-600 focus:border-green-600 focus:ring-4 focus:ring-green-600/5 outline-hidden transition-all"
                        />
                      ))}
                    </div>

                    <button
                      type="submit"
                      disabled={isVerifying || otp.some(d => !d)}
                      className="w-full bg-green-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-700 transition-all shadow-lg shadow-green-600/20 disabled:opacity-50 disabled:shadow-none"
                    >
                      {isVerifying ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>Verify & Continue</>
                      )}
                    </button>

                    <div className="text-center">
                      {timer > 0 ? (
                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                          Resend code in {timer}s
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendOTP}
                          className="text-[10px] font-black uppercase tracking-widest text-green-600 hover:text-green-700"
                        >
                          Resend Code
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}

              <p className="mt-8 text-[10px] text-zinc-400 text-center leading-relaxed font-medium">
                By continuing, you agree to receive an automated SMS for verification purposes. Standard rates apply.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
